import React, { useEffect, useMemo, useRef } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 6: Batch Service Pipeline
 * - Sources (SFTP/S3) -> Schedulers (CloudWatch/Cron)
 * - Pipeline: Ingestion -> Validation -> Processing
 * - Load to RDS, archive to S3, alert via SNS
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 8,
    fontWeight: 800,
    color: "#0f172a",
    width: 240,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0b2e59",
    width: 130,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#4a0b2a",
    width: 130,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0f3d1f",
    width: 130,
    textAlign: "center",
  },
  sched: {
    background: "#f5f3ff",
    border: "2px solid #a855f7",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#4c1d95",
    width: 120,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 8,
    fontWeight: 700,
    color: "#92400e",
    width: 160,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project6FlowDiagram() {
  const rfInstanceRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (rfInstanceRef.current) {
        rfInstanceRef.current.fitView({ padding: 0.06 });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  const nodes = useMemo(
    () => [
      // Background containers
      {
        id: "bg-pipeline",
        position: { x: 360, y: 270 },
        data: { label: "" },
        style: {
          width: 1030,
          height: 480,
          background: "#f0f9ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-ingestion",
        position: { x: 380, y: 330 },
        data: { label: "" },
        style: {
          width: 220,
          height: 150,
          background: "transparent",
          border: "2px dashed #60a5fa",
          borderRadius: 12,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-validation",
        position: { x: 660, y: 330 },
        data: { label: "" },
        style: {
          width: 240,
          height: 150,
          background: "transparent",
          border: "2px dashed #22c55e",
          borderRadius: 12,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-processing",
        position: { x: 960, y: 330 },
        data: { label: "" },
        style: {
          width: 240,
          height: 150,
          background: "transparent",
          border: "2px dashed #a855f7",
          borderRadius: 12,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Sources
      { id: "sftp", position: { x: 60, y: 60 }, data: { label: "SFTP" }, style: nodeStyle.compute },
      { id: "src-s3", position: { x: 300, y: 60 }, data: { label: "S3" }, style: nodeStyle.compute },
      { id: "file-upload", position: { x: 180, y: 170 }, data: { label: "① File Upload" }, style: nodeStyle.note },

      // Schedulers
      { id: "cw", position: { x: 430, y: 110 }, data: { label: "CloudWatch" }, style: nodeStyle.sched },
      { id: "cron", position: { x: 660, y: 110 }, data: { label: "Cron" }, style: nodeStyle.sched },
      { id: "trigger", position: { x: 540, y: 200 }, data: { label: "② Trigger" }, style: nodeStyle.note },

      // Pipeline section header
      {
        id: "sec-pipeline",
        position: { x: 410, y: 290 },
        data: { label: "AWS EC2/LAMBDA - PIPELINE" },
        style: { ...nodeStyle.section, width: 300 },
      },

      // Ingestion
      { id: "ingestion", position: { x: 420, y: 360 }, data: { label: "Ingestion" }, style: nodeStyle.section },
      { id: "bash", position: { x: 430, y: 440 }, data: { label: "Bash" }, style: nodeStyle.compute },

      // Validation
      { id: "validation", position: { x: 740, y: 360 }, data: { label: "Validation" }, style: nodeStyle.section },
      { id: "python", position: { x: 740, y: 440 }, data: { label: "Python" }, style: nodeStyle.ops },
      { id: "format", position: { x: 960, y: 440 }, data: { label: "Format" }, style: nodeStyle.ops },

      // Processing
      { id: "processing", position: { x: 1080, y: 360 }, data: { label: "Processing" }, style: nodeStyle.section },
      { id: "groovy", position: { x: 1150, y: 440 }, data: { label: "Groovy or Python or Go" }, style: nodeStyle.compute },
      { id: "loader", position: { x: 1300, y: 440 }, data: { label: "Loader" }, style: nodeStyle.compute },

      // Load to DB
      { id: "load", position: { x: 820, y: 620 }, data: { label: "③ Load" }, style: nodeStyle.note },
      { id: "rds", position: { x: 820, y: 700 }, data: { label: "DB" }, style: nodeStyle.data },

      // Archive & alerts
      { id: "archive", position: { x: 1080, y: 620 }, data: { label: "④ Archive" }, style: nodeStyle.note },
      { id: "arch-s3", position: { x: 1040, y: 700 }, data: { label: "S3" }, style: nodeStyle.compute },
      { id: "sns", position: { x: 1240, y: 700 }, data: { label: "SNS" }, style: nodeStyle.ops },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const purple = { stroke: "#7c3aed" };
    const gray = { stroke: "#64748b" };

    return [
      mkEdge("e-sftp-upload", "sftp", "file-upload", "", { style: { ...blue } }),
      mkEdge("e-s3-upload", "src-s3", "file-upload", "", { style: { ...blue } }),
      mkEdge("e-upload-cw", "file-upload", "cw", "", { style: { ...blue } }),
      mkEdge("e-upload-cron", "file-upload", "cron", "", { style: { ...blue } }),
      mkEdge("e-cw-trigger", "cw", "trigger", "② Trigger", { style: { ...green, strokeWidth: 4 } }),
      mkEdge("e-cron-trigger", "cron", "trigger", "", { style: { ...green, strokeWidth: 4 } }),
      mkEdge("e-trigger-bash", "trigger", "bash", "Ingest", { style: { ...green, strokeWidth: 4 } }),
      mkEdge("e-bash-python", "bash", "python", "Val", { style: { ...green } }),
      mkEdge("e-python-format", "python", "format", "", { style: { ...green } }),
      mkEdge("e-format-groovy", "format", "groovy", "Proc", { style: { ...purple, strokeWidth: 4 } }),
      mkEdge("e-groovy-loader", "groovy", "loader", "", { style: { ...purple } }),
      mkEdge("e-loader-load", "loader", "load", "③ Load", { style: { ...blue, strokeWidth: 5 } }),
      mkEdge("e-load-rds", "load", "rds", "", { style: { ...blue } }),
      mkEdge("e-loader-archive", "loader", "archive", "④ Archive", { style: { ...gray, strokeWidth: 4 } }),
      mkEdge("e-archive-s3", "archive", "arch-s3", "", { style: { ...gray } }),
      mkEdge("e-archive-sns", "archive", "sns", "", { style: { ...gray } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "980px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.06 }}
        onInit={(instance) => {
          rfInstanceRef.current = instance;
          setTimeout(() => instance.fitView({ padding: 0.06 }), 0);
        }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.4}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
